/* iter55p9 — Content warning modal for Dark Romance shelves.
 *
 * Intercepts a click on any bookstore genre whose row carries
 * `content_warning: true` (see bookstore_seeds.py). Warns the reader
 * about explicit content and taboo topics, suggests softer shelves
 * as an alternative, requires a "confirm 18+" checkbox to satisfy
 * stricter compliance regimes, and offers a clear retreat button.
 *
 * The reader's acceptance is remembered per-slug in localStorage so
 * repeat visits don't re-gate them.
 */
import React from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, ArrowLeft } from 'lucide-react';

const STORAGE_PREFIX = 'pw:dr-warn:';

export function hasAcceptedContentWarning(slug) {
  try { return !!window.localStorage.getItem(STORAGE_PREFIX + slug); }
  catch { return false; }
}
function rememberAcceptance(slug) {
  try { window.localStorage.setItem(STORAGE_PREFIX + slug, '1'); }
  catch { /* noop */ }
}

/**
 * Controlled modal.
 *  @prop genre         The genre row (`{ slug, name, blurb, ... }`) whose
 *                      shelf the reader tried to open. `null` hides the
 *                      modal.
 *  @prop onAcceptOnce  Called with the genre after the reader confirms
 *                      they want to proceed (remembers in localStorage,
 *                      then invokes this).
 *  @prop onRetreat     Called with the genre when the reader chooses to
 *                      back out. Parent should keep them on the current
 *                      page (or navigate to a softer shelf).
 */
export default function GenreContentWarningDialog({ genre, onAcceptOnce, onRetreat }) {
  const open = !!genre;
  // iter55p9 — Age-gate checkbox. Must be checked before the accept
  // button becomes enabled. Resets to false every time the modal opens
  // (so a genre re-open re-prompts the confirmation).
  const [ageConfirmed, setAgeConfirmed] = React.useState(false);
  React.useEffect(() => { if (open) setAgeConfirmed(false); }, [open, genre?.slug]);
  // iter-deeplink-gate-fix — Radix's AlertDialogAction auto-closes the
  // dialog on click, which fires `onOpenChange(false)` synchronously —
  // before `handleAccept`'s awaited fetch resolves. Without this guard
  // that spuriously calls `handleRetreat` first on every Accept click.
  // Harmless for callers whose `onRetreat` just clears local state, but
  // breaks callers (e.g. a direct genre-page deep link) whose retreat
  // navigates away, since it would fire before `onAcceptOnce` does.
  const acceptingRef = React.useRef(false);
  React.useEffect(() => { if (open) acceptingRef.current = false; }, [open, genre?.slug]);

  const handleAccept = async () => {
    if (!genre || !ageConfirmed) return;
    acceptingRef.current = true;
    // iter55p10 — Persist acceptance to the audit log BEFORE we
    // hand back to the parent. Best-effort — even if the POST
    // fails (offline, throttled), we still open the shelf so the
    // reader isn't blocked. The admin log tail will show any
    // dropped records.
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      await fetch(`${API}/content-warning/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: genre.slug,
          confirmed_18: true,
          source: 'bookstore-landing',
        }),
      });
    } catch { /* silent — offline / throttled / logged-out */ }
    rememberAcceptance(genre.slug);
    onAcceptOnce?.(genre);
  };
  const handleRetreat = () => {
    if (!genre || acceptingRef.current) return;
    onRetreat?.(genre);
  };
  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next) handleRetreat(); }}>
      {/* mobile-scroll-fix — removed overflow-hidden (was blocking scroll on tall phones).
          Base AlertDialogContent now uses max-h-[calc(100dvh-2rem)] + overflow-y-auto.
          This dialog wraps the body in a flex column so the footer stays pinned. */}
      <AlertDialogContent
        data-testid="genre-content-warning-dialog"
        className="flex max-h-[calc(100dvh-2rem)] max-w-xl flex-col overscroll-contain [&_*]:min-w-0 [overflow-wrap:anywhere]"
      >
        <div className="flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <span className="truncate">{genre?.name || 'Content warning'}</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-[13px] leading-relaxed">
              <span className="block">
                Titles on the <strong>{genre?.name}</strong> shelf contain <strong>explicit adult content</strong>
                — including scenes of coercive intimacy (dub-con / non-con), graphic violence, obsessive or
                possessive relationship dynamics, and topics that some readers find disturbing or
                objectionable due to cultural taboos and prevailing societal norms.
              </span>
              <span className="block">
                These books are written for adult readers who actively seek out dark themes as a form of
                catharsis. They are not intended as a portrayal of healthy real-world relationships.
              </span>
              <span className="block rounded-sm border border-amber-500/40 bg-amber-500/10 p-2 text-[12px]">
                <strong>If explicit content isn&apos;t for you</strong>, our
                <em> Romance</em>, <em>Literary Fiction</em>, and <em>Mystery &amp; Thriller</em> shelves offer
                character-driven love stories and page-turners without the darker content on this
                shelf. Use the <em>Take me somewhere gentler</em> button below to head back — no shelf
                opens until you tap <em>continue</em>.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* iter55p9 — 18+ age-gate checkbox */}
          <label
            className="mt-3 flex cursor-pointer select-none items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/5 p-2 text-[12.5px] leading-snug"
            data-testid="genre-content-warning-age-gate-label"
          >
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={(e) => setAgeConfirmed(e.target.checked)}
              data-testid="genre-content-warning-age-gate"
              className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-amber-700"
            />
            <span className="min-w-0 flex-1">
              I confirm I am <strong>18 years or older</strong> and voluntarily choose to view
              explicit adult content.
            </span>
          </label>
        </div>

        {/* Sticky footer — always visible even when dialog body scrolls */}
        <AlertDialogFooter className="shrink-0 border-t border-border/40 pt-3 flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2">
          <AlertDialogCancel
            onClick={handleRetreat}
            data-testid="genre-content-warning-retreat"
            className="inline-flex min-w-0 items-center gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Take me somewhere gentler</span>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleAccept}
            disabled={!ageConfirmed}
            data-testid="genre-content-warning-accept"
            className="min-w-0 bg-amber-700 hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="truncate">Continue to {genre?.name || 'shelf'}</span>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
